import React, { useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { rangoDe } from "../../managment/enrollmentRequests/filtrosMatriculas";
import "../../managment/enrollmentRequests/filtrosMatriculas.css";

/*
	Barra de filtros de Promociones con las mismas piezas que la de Matrículas (Esteban, 2026-11-01: "que se parezca a Matrículas"):
	Hoy · Ayer · Esta Semana · Este Mes · Rango, y Promoción / Asesora como pastillas. El servidor filtra una promoción y una asesora
	a la vez, así que aquí se elige una (la pastilla se ve igual que la de Matrículas).
*/
export type PresetFecha = "" | "hoy" | "ayer" | "semana" | "mes" | "rango";
export interface Filtros { preset: PresetFecha; from: string; to: string; promotionId: string; advisorId: string }
export const FILTROS_VACIOS: Filtros = { preset: "", from: "", to: "", promotionId: "", advisorId: "" };
export const hayFiltros = (f: Filtros) => !!(f.from || f.to || f.promotionId || f.advisorId);
/** «Hoy» y «Esta Semana» se recalculan al pedir: si la pantalla quedó abierta desde ayer, siguen siendo hoy. */
export const fechasDe = (f: Filtros): { from: string; to: string } => {
	if (f.preset && f.preset !== "rango") {
		const r = rangoDe(f.preset);
		return { from: r.dateFrom, to: r.dateTo };
	}
	return { from: f.from, to: f.to };
};

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const corta = (s: string) => {
	const [y, m, d] = s.split("-").map(Number);
	return y ? `${d} ${MESES[m - 1]}` : "";
};
const sinAcentos = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

export const Una: React.FC<{ titulo: string; opciones: { id: number; name: string }[]; valor: string; onChange: (v: string) => void }> = ({ titulo, opciones, valor, onChange }) => {
	const [q, setQ] = useState("");
	const visibles = useMemo(() => {
		const t = sinAcentos(q.trim());
		return t === "" ? opciones : opciones.filter((o) => sinAcentos(o.name).includes(t));
	}, [q, opciones]);
	const elegida = opciones.find((o) => String(o.id) === valor);
	return (
		<Dropdown className="waFm" onToggle={(s) => { if (!s) setQ(""); }}>
			<Dropdown.Toggle as="button" type="button" className={"waFmBoton" + (valor ? " activo" : "")}>
				<span className="waFmTitulo">{titulo}</span>
				{valor ? <span className="waFmValor">{elegida?.name || "1"}</span> : null}
				<FontAwesomeIcon icon={solid("chevron-down")} className="waFmFlecha" />
			</Dropdown.Toggle>
			<Dropdown.Menu className="waFmMenu" popperConfig={{ strategy: "fixed" }} renderOnMount>
				{opciones.length > 8 ? (
					<div className="waFmBuscar">
						<FontAwesomeIcon icon={solid("magnifying-glass")} />
						<input autoFocus type="text" placeholder="Buscar" value={q} onChange={(e) => setQ(e.target.value)} />
					</div>
				) : null}
				<div className="waFmLista">
					{visibles.length < 1 ? <div className="waFmVacio">Sin resultados</div> : visibles.map((o) => (
						<Dropdown.Item as="button" type="button" key={o.id} className={"waFmOpcion" + (String(o.id) === valor ? " elegida" : "")}
							onClick={() => onChange(String(o.id) === valor ? "" : String(o.id))}>
							<span className="waFmNombre">{o.name}</span>
							{String(o.id) === valor ? <FontAwesomeIcon icon={solid("check")} style={{ color: "var(--wa-acento)" }} /> : null}
						</Dropdown.Item>
					))}
				</div>
				{valor ? (
					<div className="waFmPie">
						<button type="button" className="btn btn-link btn-sm" onClick={() => onChange("")}>Quitar</button>
					</div>
				) : null}
			</Dropdown.Menu>
		</Dropdown>
	);
};

export interface FiltrosPromosProps {
	valor: Filtros;
	onChange: (v: Filtros) => void;
	/** Sin fechas en Por Aplicar (lo pendiente no tiene fecha de pago). */
	conFechas: boolean;
	etiquetaFecha: string;
	promociones: { id: number; name: string }[];
	asesoras: { id: number; name: string }[];
	/** Algo a la izquierda de las pastillas (Anuladas · Fallidas). */
	antes?: React.ReactNode;
}

const FiltrosPromos: React.FC<FiltrosPromosProps> = ({ valor, onChange, conFechas, etiquetaFecha, promociones, asesoras, antes }) => {
	const [desde, setDesde] = useState(valor.from);
	const [hasta, setHasta] = useState(valor.to);
	const [abierto, setAbierto] = useState(false);
	const elegirPreset = (p: PresetFecha) => {
		if (valor.preset === p) {
			onChange({ ...valor, preset: "", from: "", to: "" });
			return;
		}
		const r = rangoDe(p);
		onChange({ ...valor, preset: p, from: r.dateFrom, to: r.dateTo });
	};
	const aplicarRango = () => {
		const [a, b] = desde && hasta && desde > hasta ? [hasta, desde] : [desde, hasta || desde];
		onChange({ ...valor, preset: a ? "rango" : "", from: a, to: b });
		setAbierto(false);
	};
	const textoRango = valor.preset === "rango" && valor.from ? (valor.from === valor.to ? corta(valor.from) : `${corta(valor.from)} – ${corta(valor.to)}`) : "";
	const presets: { key: PresetFecha; label: string }[] = [{ key: "hoy", label: "Hoy" }, { key: "ayer", label: "Ayer" }, { key: "semana", label: "Esta Semana" }, { key: "mes", label: "Este Mes" }];

	return (
		<div className="waFmBarra">
			<div className="waFmBarraFecha">
				{antes}
				{conFechas ? <>
					<span className="waFmBarraEtiqueta">{etiquetaFecha}</span>
					<div className="waFmSegs">
						{presets.map((p) => (
							<button key={p.key} type="button" className={"waFmSeg" + (valor.preset === p.key ? " activo" : "")} onClick={() => elegirPreset(p.key)}>{p.label}</button>
						))}
						<Dropdown show={abierto} onToggle={(s) => { setAbierto(s); if (s) { setDesde(valor.from); setHasta(valor.to); } }} autoClose="outside">
							<Dropdown.Toggle as="button" type="button" className={"waFmSeg waFmSegRango" + (valor.preset === "rango" ? " activo" : "")}>
								<FontAwesomeIcon icon={solid("calendar-days")} />&nbsp;{textoRango || "Rango"}
							</Dropdown.Toggle>
							<Dropdown.Menu className="waFmMenu waFmRangoMenu" popperConfig={{ strategy: "fixed" }} renderOnMount>
								<div className="waFmRangoCampos">
									<label>Desde<input type="date" value={desde} max={hasta || undefined} onChange={(e) => setDesde(e.target.value)} /></label>
									<label>Hasta<input type="date" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)} /></label>
								</div>
								<div className="waFmRangoPie">
									<button type="button" className="btn btn-link btn-sm" onClick={() => { setDesde(""); setHasta(""); }}>Borrar</button>
									<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={!desde} onClick={aplicarRango}>Aplicar</button>
								</div>
							</Dropdown.Menu>
						</Dropdown>
					</div>
				</> : null}
			</div>
			<div className="waFmBarraListas">
				<Una titulo="Promoción" opciones={promociones} valor={valor.promotionId} onChange={(v) => onChange({ ...valor, promotionId: v })} />
				<Una titulo="Asesora" opciones={asesoras} valor={valor.advisorId} onChange={(v) => onChange({ ...valor, advisorId: v })} />
				{hayFiltros(valor) ? <button type="button" className="btn btn-link btn-sm waFmBarraLimpiar" onClick={() => onChange(FILTROS_VACIOS)}>Limpiar Filtros</button> : null}
			</div>
		</div>
	);
};

export default FiltrosPromos;
